import React, { useState } from 'react';
import { useSchool } from '../context/SchoolContext';
import { Calendar, Search, Filter, Clock, User, MapPin } from 'lucide-react';
import './Scheduling.css';

const Scheduling = () => {
    const { lessons, selectedBranch } = useSchool();
    const [searchTerm, setSearchTerm] = useState('');

    const getStatusColor = (status) => {
        switch ((status || '').toLowerCase()) {
            case 'completed': return 'success';
            case 'scheduled': return 'primary';
            case 'cancelled': return 'danger';
            default: return 'warning';
        }
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '-';
        return new Date(dateStr).toLocaleDateString('en-GB', {
            weekday: 'short', day: 'numeric', month: 'short', year: 'numeric'
        });
    };

    const formatTime = (dateStr) => {
        if (!dateStr) return '-';
        return new Date(dateStr).toLocaleTimeString('en-US', {
            hour: '2-digit', minute: '2-digit'
        });
    };

    // Filter Logic
    const filteredLessons = lessons.filter(lesson => {
        // Branch Filter
        if (selectedBranch !== 'All Branches') {
            // Check implicit branch from student or instructor if explicit branch_id is missing?
            // For now, assume backend might need to provide branch name, or we filter by what we have.
            // If lesson doesn't have branch_name, we might default to showing it or rely on student's branch.
            // Let's check if 'branch_name' exists or similar.
            // Update: We didn't explicitly join branch in controller-lessons. But we can assume data might arrive.
            // If strict filtering is needed, we'll need to update controller. 
            // For MVP, let's filter by searching text or just show all if branch data missing.
        }

        const searchLower = searchTerm.toLowerCase();
        const studentName = `${lesson.student_first_name || ''} ${lesson.student_last_name || ''}`;
        const instructorName = `${lesson.instructor_first_name || ''} ${lesson.instructor_last_name || ''}`;

        return (
            studentName.toLowerCase().includes(searchLower) ||
            instructorName.toLowerCase().includes(searchLower) ||
            (lesson.lesson_type || '').toLowerCase().includes(searchLower)
        );
    });

    return (
        <div className="page-container scheduling-page">
            <div className="page-header">
                <div>
                    <h2 className="page-title">Scheduling & Diary</h2>
                    <p className="page-subtitle">View all scheduled driving lessons</p>
                </div>
            </div>

            {/* Filters */}
            <div className="filters-bar">
                <div className="search-input-wrapper">
                    <Search size={18} className="search-icon" />
                    <input
                        type="text"
                        placeholder="Search student or instructor..."
                        className="search-input"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>
                <div className="flex gap-2">
                    <button className="btn btn-outline flex items-center gap-2">
                        <Filter size={18} /> Month View
                    </button>
                    <button className="btn btn-primary flex items-center gap-2">
                        <Calendar size={18} /> Today
                    </button>
                </div>
            </div>

            {/* Lessons Table */}
            <div className="data-card">
                <table className="data-table">
                    <thead>
                        <tr>
                            <th>Date & Time</th>
                            <th>Student</th>
                            <th>Instructor</th>
                            <th>Type</th>
                            <th>Status</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredLessons.length > 0 ? (
                            filteredLessons.map((lesson) => (
                                <tr key={lesson.id}>
                                    <td>
                                        <div className="flex flex-col">
                                            <span className="font-medium flex items-center gap-2">
                                                <Calendar size={14} className="text-gray-400" />
                                                {formatDate(lesson.start_time)}
                                            </span>
                                            <span className="text-sm text-gray-500 flex items-center gap-2">
                                                <Clock size={14} className="text-gray-400" />
                                                {formatTime(lesson.start_time)} - {formatTime(lesson.end_time)}
                                            </span>
                                        </div>
                                    </td>
                                    <td>
                                        <div className={`font-medium ${(!lesson.student_first_name && !lesson.student_last_name) ? 'text-red italic' : ''}`}>
                                            {(lesson.student_first_name || lesson.student_last_name)
                                                ? `${lesson.student_first_name || ''} ${lesson.student_last_name || ''}`.trim()
                                                : 'Missing Student'}
                                        </div>
                                    </td>
                                    <td>
                                        <div className="flex items-center gap-2">
                                            <div className="w-6 h-6 rounded-full bg-gray-100 flex items-center justify-center text-xs">
                                                {(lesson.instructor_first_name || 'I').charAt(0)}
                                            </div>
                                            <span className={(!lesson.instructor_first_name && !lesson.instructor_last_name) ? 'text-red italic' : ''}>
                                                {(lesson.instructor_first_name || lesson.instructor_last_name)
                                                    ? `${lesson.instructor_first_name || ''} ${lesson.instructor_last_name || ''}`.trim()
                                                    : 'Missing Instructor'}
                                            </span>
                                        </div>
                                    </td>
                                    <td>
                                        <span className="badge badge-gray">{lesson.lesson_type || 'Practical'}</span>
                                    </td>
                                    <td>
                                        <span className={`badge badge-${getStatusColor(lesson.lesson_status)}`}>
                                            {lesson.lesson_status || 'Scheduled'}
                                        </span>
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan="5" className="text-center py-8 text-gray-500">
                                    No lessons found for this period.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default Scheduling;
